import { useState, useEffect, useCallback } from 'react';
import { authService } from '../services';
import { permissionService } from '../services';

/**
 * Hook to check user permissions
 */
export const usePermissions = () => {
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = authService.getUserId();
    if (userId) {
      loadPermissions(userId);
    } else {
      setLoading(false);
    }
  }, []);

  const loadPermissions = async (userId) => {
    try {
      setLoading(true);
      const permissions = await permissionService.getUserPermissions(userId);
      setUserPermissions(permissions);
    } catch (err) {
      console.error('Error loading user permissions:', err);
      setUserPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has a specific permission (stable ref — keeps nav ctx from re-rendering every frame)
   */
  const hasPermission = useCallback((permissionCode) => {
    if (!permissionCode) return false;

    const user = authService.getUser();
    const userRole = (user?.roleName || user?.RoleName || "").toLowerCase();
    if (userRole === 'super admin') {
      return true;
    }

    const token = authService.getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const permissionsClaim = payload.Permissions || payload.permissions;
        if (permissionsClaim) {
          const permissions = typeof permissionsClaim === 'string'
            ? permissionsClaim.split(',').map(p => p.trim())
            : permissionsClaim;
          if (permissions.includes(permissionCode)) {
            return true;
          }
        }
      } catch (err) {
        // Token parsing failed, continue to check loaded permissions
      }
    }

    return userPermissions.includes(permissionCode);
  }, [userPermissions]);

  const hasAnyPermission = useCallback((permissionCodes) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
      return false;
    }
    return permissionCodes.some(code => hasPermission(code));
  }, [hasPermission]);

  const hasAllPermissions = useCallback((permissionCodes) => {
    if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
      return false;
    }
    return permissionCodes.every(code => hasPermission(code));
  }, [hasPermission]);

  return {
    permissions: userPermissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshPermissions: () => {
      const userId = authService.getUserId();
      if (userId) {
        loadPermissions(userId);
      }
    },
  };
};

/**
 * Higher-order component to protect routes/components based on permissions
 */
export const withPermission = (Component, requiredPermission) => {
  return (props) => {
    const { hasPermission, loading } = usePermissions();
    
    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!hasPermission(requiredPermission)) {
      return (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <h3>Access Denied</h3>
          <p>You do not have permission to access this page.</p>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};

export default usePermissions;

